import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Heart, Instagram, Music2, Mail, ArrowRight, AlertTriangle } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="about-page" data-testid="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <motion.div
          className="about-hero-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="about-tag">About BlackGirlTrading-Academy</span>
          <h1>Hi, I'm <span className="highlight">Adiajah</span></h1>
          <p className="about-handle">@BlackGirlsTrade_</p>
          <p className="about-intro">
            A beginner trader documenting my journey while learning through my online trading school called <strong>BlackGirlTrading-Academy</strong>.
          </p>
        </motion.div>
      </section>

      {/* Story Section */}
      <section className="about-story">
        <motion.div
          className="story-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2>My Story</h2>
          
          <div className="story-text">
            <p>
              I created this platform because there are not many visible Black women in the trading space, 
              and even fewer openly learning in real time. Instead of pretending to be an expert, I've chosen 
              <strong> transparency</strong>.
            </p>
            
            <p>
              I'm sharing what I learn, the strategies I study, and the process of developing trading discipline 
              step by step. This website and community are a space for people who want to <strong>learn alongside me</strong>.
            </p>
            
            <p>
              I built it using my own limited coding skills along with various AI tools to organise resources, 
              track progress, and make the learning process more accessible for others who may be starting from 
              zero — just like I am.
            </p>
          </div>

          {/* Disclaimer Box */}
          <div className="about-disclaimer">
            <div className="disclaimer-icon">
              <AlertTriangle size={24} />
            </div>
            <div className="disclaimer-content">
              <h3>Important Disclaimer</h3>
              <p>
                <strong>I am not a financial advisor and I do not guarantee profits.</strong> Trading involves 
                significant risk, and anyone choosing to participate should do their own research and take 
                responsibility for their financial decisions. What I share are strategies, educational insights, 
                and my personal learning journey — <strong>not financial advice</strong>.
              </p>
            </div>
          </div>

          <div className="story-text">
            <p className="mission-statement">
              <Heart className="heart-icon" size={20} />
              <strong>My goal is simple:</strong> learn, document the process, build discipline, and show that 
              everyday people — including Black women — belong in the world of trading.
            </p>
          </div>
        </motion.div>
      </section>

      {/* My Full Story Section */}
      <section className="about-full-story">
        <motion.div
          className="full-story-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2>My Story</h2>
          
          <div className="story-paragraphs">
            <p>
              My name is Adiajah, and I'm the creator of <strong>BlackGirlTrading-Academy</strong> and the voice behind <strong>Black Girl Trades</strong>.
            </p>
            
            <p>
              I began learning trading because I wanted to understand how financial markets work and develop a skill that requires discipline, patience, and strategy. Like many beginners, I started with curiosity and a desire to expand my financial knowledge and build greater independence in the long term.
            </p>
            
            <p>
              Alongside this journey, my life has been shaped by a variety of experiences and interests. I work within the property sector, and I've always been drawn to understanding systems—whether that's how markets work, how people think, or how societies develop. Outside of work, I have a deep love for travel, culture, languages, philosophy, and food. Exploring different places and ways of thinking has always been important to me.
            </p>
            
            <p>
              I also have strong ties to <strong>Uganda</strong>, where some of my charity work has involved supporting children and local orphanages. Those experiences have deeply influenced my outlook on life and strengthened my belief in building opportunities, independence, and stability—not just for myself, but for others where possible.
            </p>
            
            <p>
              While exploring the trading space, I noticed that there were very few visible Black women openly involved in trading, and even fewer documenting the learning process in a transparent way. That absence of representation made me realise how important it is for people to see others who look like them participating in spaces that often feel exclusive or difficult to access.
            </p>
            
            <p>
              <strong>BlackGirlTrading-Academy</strong> was created as a platform where I could organise what I'm learning and share that journey with others. Using my own limited coding knowledge alongside various AI tools, I built this site to document my progress, track my learning, and create a space where other beginners can learn alongside me.
            </p>
            
            <p>
              Rather than presenting myself as an expert, my goal is to be transparent about the process. Trading is a skill that takes time, discipline, and consistent learning. By documenting my journey publicly, I hope to show the real path of learning—including the challenges, lessons, and growth that come with it.
            </p>
            
            <p className="story-vision">
              My long-term vision is simple: to build financial independence, continue learning, and show that everyday people — including Black women — belong in the world of financial markets.
            </p>
            
            <p className="story-tagline">
              <strong>BlackGirlTrading-Academy</strong> and <strong>Black Girl Trades</strong> are built around a simple idea: <em>learning openly, building discipline, and growing together.</em>
            </p>
          </div>
        </motion.div>
      </section>

      {/* Social Links Section */}
      <section className="about-social">
        <h2>Follow My Journey</h2>
        <p>Connect with me on social media for daily updates, tips, and real-time learning moments.</p>
        
        <div className="social-links">
          <a 
            href="https://www.tiktok.com/@BlackGirlsTrade_" 
            target="_blank" 
            rel="noopener noreferrer"
            className="social-card tiktok"
            data-testid="tiktok-link"
          >
            <Music2 size={32} />
            <span className="social-platform">TikTok</span>
            <span className="social-handle">@BlackGirlsTrade_</span>
          </a>
          
          <a 
            href="https://www.instagram.com/BlackGirlsTrade_" 
            target="_blank" 
            rel="noopener noreferrer"
            className="social-card instagram"
            data-testid="instagram-link"
          >
            <Instagram size={32} />
            <span className="social-platform">Instagram</span>
            <span className="social-handle">@BlackGirlsTrade_</span>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <div className="cta-content">
          <h2>Ready to Start Learning?</h2>
          <p>Join the BlackGirlTrading-Academy community and begin your trading journey today.</p>
          <div className="cta-buttons">
            <Link to="/learn" className="btn btn-primary" data-testid="start-learning-btn">
              Start Learning <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="btn btn-outline" data-testid="contact-btn">
              <Mail size={18} /> Get in Touch
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        .about-page {
          min-height: 100vh;
        }

        /* Hero Section */
        .about-hero {
          padding: 80px 24px 60px;
          text-align: center;
          background: linear-gradient(180deg, rgba(0, 255, 148, 0.05) 0%, transparent 100%);
        }

        .about-hero-content {
          max-width: 700px;
          margin: 0 auto;
        }

        .about-tag {
          display: inline-block;
          background: rgba(0, 255, 148, 0.1);
          color: var(--primary);
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 500;
          margin-bottom: 24px;
        }

        .about-hero h1 {
          font-size: clamp(2.5rem, 6vw, 4rem);
          margin-bottom: 12px;
          font-weight: 700;
        }

        .about-hero h1 .highlight {
          color: var(--primary);
        }

        .about-handle {
          font-size: 1.25rem;
          color: var(--primary);
          font-weight: 600;
          margin-bottom: 24px;
        }

        .about-intro {
          font-size: 1.125rem;
          color: var(--text-secondary);
          line-height: 1.7;
        }

        .about-intro strong {
          color: var(--text-primary);
        }

        /* Story Section */
        .about-story {
          padding: 60px 24px;
          max-width: 800px;
          margin: 0 auto;
        }

        .story-content h2 {
          font-size: 2rem;
          margin-bottom: 32px;
          text-align: center;
        }

        .story-text {
          margin-bottom: 32px;
        }

        .story-text p {
          font-size: 1.0625rem;
          color: var(--text-secondary);
          line-height: 1.8;
          margin-bottom: 20px;
        }

        .story-text strong {
          color: var(--text-primary);
        }

        .about-disclaimer {
          display: flex;
          gap: 20px;
          background: rgba(255, 200, 50, 0.08);
          border: 1px solid rgba(255, 200, 50, 0.3);
          border-radius: 12px;
          padding: 24px;
          margin: 32px 0;
        }

        .disclaimer-icon {
          color: #ffc832;
          flex-shrink: 0;
        }

        .disclaimer-content h3 {
          font-size: 1rem;
          margin-bottom: 8px;
          color: #ffc832;
        }

        .disclaimer-content p {
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.7;
          margin: 0;
        }

        .disclaimer-content strong {
          color: var(--text-primary);
        }

        .mission-statement {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          background: rgba(0, 255, 148, 0.08);
          border-radius: 12px;
          padding: 24px;
          font-size: 1.0625rem !important;
        }

        .heart-icon {
          color: var(--primary);
          flex-shrink: 0;
          margin-top: 4px;
        }

        /* Full Story Section */
        .about-full-story {
          padding: 80px 24px;
          background: var(--bg-subtle);
        }

        .full-story-content {
          max-width: 800px;
          margin: 0 auto;
        }

        .full-story-content h2 {
          font-size: 2rem;
          margin-bottom: 32px;
          text-align: center;
        }

        .story-paragraphs {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .story-paragraphs p {
          font-size: 1.0625rem;
          color: var(--text-secondary);
          line-height: 1.8;
        }

        .story-paragraphs strong {
          color: var(--primary);
        }

        .story-vision {
          background: rgba(0, 255, 148, 0.08);
          border-left: 4px solid var(--primary);
          padding: 20px 24px;
          border-radius: 0 12px 12px 0;
          margin-top: 12px;
        }

        .story-tagline {
          text-align: center;
          font-size: 1.125rem !important;
          padding: 24px;
          background: var(--bg-paper);
          border: 1px solid var(--border);
          border-radius: 12px;
          margin-top: 12px;
        }

        .story-tagline em {
          color: var(--primary);
        }

        /* Social Section */
        .about-social {
          padding: 60px 24px;
          text-align: center;
          background: var(--bg-subtle);
        }

        .about-social h2 {
          font-size: 2rem;
          margin-bottom: 12px;
        }

        .about-social > p {
          color: var(--text-muted);
          margin-bottom: 32px;
        }

        .social-links {
          display: flex;
          justify-content: center;
          gap: 24px;
          flex-wrap: wrap;
        }

        .social-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
          padding: 32px 48px;
          border-radius: 16px;
          text-decoration: none;
          transition: all 0.3s ease;
          min-width: 200px;
        }

        .social-card.tiktok {
          background: linear-gradient(135deg, rgba(0, 0, 0, 0.9) 0%, rgba(30, 30, 30, 0.9) 100%);
          border: 1px solid #333;
        }

        .social-card.tiktok:hover {
          border-color: #ff0050;
          box-shadow: 0 8px 32px rgba(255, 0, 80, 0.2);
        }

        .social-card.tiktok svg {
          color: #ff0050;
        }

        .social-card.instagram {
          background: linear-gradient(135deg, rgba(131, 58, 180, 0.2) 0%, rgba(253, 29, 29, 0.2) 50%, rgba(252, 176, 69, 0.2) 100%);
          border: 1px solid rgba(253, 29, 29, 0.3);
        }

        .social-card.instagram:hover {
          border-color: rgba(253, 29, 29, 0.6);
          box-shadow: 0 8px 32px rgba(253, 29, 29, 0.2);
        }

        .social-card.instagram svg {
          color: #fd1d1d;
        }

        .social-platform {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .social-handle {
          font-size: 14px;
          color: var(--text-muted);
        }

        /* CTA Section */
        .about-cta {
          padding: 80px 24px;
          text-align: center;
        }

        .cta-content {
          max-width: 600px;
          margin: 0 auto;
        }

        .about-cta h2 {
          font-size: 2rem;
          margin-bottom: 16px;
        }

        .about-cta > .cta-content > p {
          color: var(--text-muted);
          margin-bottom: 32px;
        }

        .cta-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .cta-buttons .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 14px 28px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--primary);
          color: #0a0a0f;
          border: none;
        }

        .btn-primary:hover {
          background: var(--primary-light);
        }

        .btn-outline {
          background: transparent;
          border: 1px solid var(--border);
          color: var(--text-secondary);
        }

        .btn-outline:hover {
          border-color: var(--primary);
          color: var(--primary);
        }

        @media (max-width: 600px) {
          .about-hero {
            padding: 60px 20px 40px;
          }

          .about-disclaimer {
            flex-direction: column;
            gap: 12px;
          }

          .social-card {
            width: 100%;
            padding: 24px;
          }

          .cta-buttons {
            flex-direction: column;
          }

          .cta-buttons .btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default AboutPage;
